/** One card of the person record (plan "Page anatomy" → Cards) — the records core's
 *  `RecordPanel` (Editable fields, the muted "From the app / OTA" vendor block, the System
 *  block) over the person's FieldSlots. The card is its section's anchor — the number keys
 *  scroll to it and focus it. A card may render a group of its own (`renderGroup` — the
 *  contacts list) and add facts that are not catalog fields (`afterSystem` — Access's
 *  sign-in facts, Technical's internal properties). */
import type { ReactNode } from 'react';

import type { UserRecordSection } from '@/api/userRecord';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { FieldCellGrid } from '@/features/records/fields/FieldCellGrid';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import { FIELD_GRID_SX } from '@/features/records/panels/fieldGrid';
import { RecordPanel, type RecordFieldGroup } from '@/features/records/panels/RecordPanel';
import type { ViewFieldRef, ViewSection } from '@/features/records/views/types';

import { userSectionAnchorId } from '../views/userLayout';

import { userEditableDisplay, userFieldActions, userFieldDisplay, type UserDisplayContext } from './displays';

/** Widgets whose input wants the whole row. */
const WIDE_WIDGETS = new Set(['tags', 'textarea', 'contacts']);
/** Read-only values that read as several lines. */
const WIDE_KEYS = new Set(['ota']);

export interface UserSectionPanelProps {
  section: ViewSection<UserRecordSection>;
  display: UserDisplayContext;
  /** Right side of the card header (the contacts' "Add contact"). */
  meta?: ReactNode;
  /** Renderings that replace the generic one for a key. */
  displays?: Partial<Record<string, ReactNode>>;
  /** Label overrides by key. */
  labels?: Partial<Record<string, string>>;
  groupEditable?: (fields: ViewFieldRef[]) => RecordFieldGroup[];
  /** A group the card renders itself; undefined = the field grid. */
  renderGroup?: (group: RecordFieldGroup) => ReactNode | undefined;
  systemTitle?: { title: string; note?: string };
  afterEditable?: ReactNode;
  afterVendor?: ReactNode;
  afterSystem?: ReactNode;
}

export function UserSectionPanel({ section, display, displays, labels, renderGroup, ...rest }: UserSectionPanelProps) {
  return (
    <RecordPanel
      section={section}
      anchorId={userSectionAnchorId(section.id)}
      renderFields={(g) =>
        renderGroup?.(g) ?? <UserFieldGrid fields={g.fields} display={display} displays={displays} labels={labels} />
      }
      {...rest}
    />
  );
}

export function UserFieldGrid({
  fields,
  display,
  displays,
  labels,
}: {
  fields: ViewFieldRef[];
  display: UserDisplayContext;
  displays?: Partial<Record<string, ReactNode>> | undefined;
  labels?: Partial<Record<string, string>> | undefined;
}) {
  const { fieldByKey } = useRecordEditor();
  return (
    <FieldCellGrid
      sx={FIELD_GRID_SX}
      cells={fields.map((f) => {
        const field = fieldByKey.get(f.key);
        const readOnly = field?.kind === 'vendor' || field?.kind === 'derived';
        const override = displays?.[f.key];
        const shown =
          override !== undefined
            ? override
            : readOnly
              ? userFieldDisplay(f.key, display)
              : userEditableDisplay(f.key, display);
        const actions = readOnly ? undefined : userFieldActions(f.key);
        const label = labels?.[f.key];
        // An input keeps its width in both modes; a read-only list takes the row only when
        // it has something to list.
        const wide =
          Boolean(field?.widget && WIDE_WIDGETS.has(field.widget)) ||
          (WIDE_KEYS.has(f.key) && shown !== null && shown !== undefined && shown !== '');
        return {
          key: `${f.entity}:${f.id}:${f.key}`,
          rec: `${f.entity}:${f.id}`,
          path: f.path,
          wide,
          node: (
            <FieldSlot
              fieldKey={f.key}
              entity={f.entity}
              id={f.id}
              path={f.path}
              inlineChoices={false}
              {...(label ? { label } : {})}
              {...(shown !== undefined ? { display: shown } : {})}
              {...(actions ? { actions } : {})}
            />
          ),
        };
      })}
    />
  );
}
